import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

export type Audience = {
  id: string;
  branch_id: string | null;
  job_title_id: string | null;
  branches: { name: string } | null;
  job_titles: { name: string } | null;
};

export type ChannelPerson = {
  employee_id: string;
  employees: { first_name: string; last_name: string } | null;
};

export type AdminChannel = {
  id: string;
  name: string;
  description: string | null;
  is_announcement_only: boolean;
  /** ההודעה מגיעה רק למי שבמשמרת ברגע השליחה (מיגרציה 087). */
  on_shift_only: boolean;
  is_archived: boolean;
  created_at: string;
  channel_audiences: Audience[];
  channel_members_extra: ChannelPerson[];
};

/** מה שמסך העריכה שולח. ריק בכל השדות אינו חוקי: ערוץ חייב קהל. */
export type ChannelDraft = {
  id: string | null;
  name: string;
  description: string;
  isAnnouncementOnly: boolean;
  onShiftOnly: boolean;
  wholeNetwork: boolean;
  branchIds: string[];
  jobTitleIds: string[];
  employeeIds: string[];
};

export function emptyDraft(): ChannelDraft {
  return {
    id: null,
    name: '',
    description: '',
    isAnnouncementOnly: false,
    onShiftOnly: false,
    wholeNetwork: false,
    branchIds: [],
    jobTitleIds: [],
    employeeIds: [],
  };
}

/**
 * ערוץ קיים כטיוטה לעריכה.
 *
 * הכללים נשמרים כמכפלה של סניפים בתפקידים, ולכן הדרך חזרה היא
 * איסוף הערכים הייחודיים בכל ציר. "כל הרשת" הוא כלל יחיד ריק
 * בשניהם, ולא היעדר כללים.
 */
export function draftOf(channel: AdminChannel): ChannelDraft {
  const rules = channel.channel_audiences;
  const wholeNetwork =
    rules.length === 1 && rules[0].branch_id === null && rules[0].job_title_id === null;

  return {
    id: channel.id,
    name: channel.name,
    description: channel.description ?? '',
    isAnnouncementOnly: channel.is_announcement_only,
    onShiftOnly: channel.on_shift_only,
    wholeNetwork,
    branchIds: wholeNetwork ? [] : unique(rules.map((rule) => rule.branch_id)),
    jobTitleIds: wholeNetwork ? [] : unique(rules.map((rule) => rule.job_title_id)),
    employeeIds: channel.channel_members_extra.map((person) => person.employee_id),
  };
}

function unique(values: (string | null)[]): string[] {
  return [...new Set(values.filter((value): value is string => value !== null))];
}

const KEY = ['admin-channels'] as const;

export function useAdminChannels() {
  return useQuery({
    queryKey: KEY,
    queryFn: async (): Promise<AdminChannel[]> => {
      // kind = channel: שיחות אישיות אינן ערוצים שמנהלים כאן, וגם
      // השיחות של המנהל עצמו לא אמורות להופיע במסך ההגדרות.
      const { data, error } = await supabase
        .from('channels')
        .select(
          `id, name, description, is_announcement_only, on_shift_only, is_archived, created_at,
           channel_audiences ( id, branch_id, job_title_id,
             branches ( name ), job_titles ( name ) ),
           channel_members_extra ( employee_id, employees ( first_name, last_name ) )`,
        )
        .eq('kind', 'channel')
        .order('is_archived')
        .order('name');
      if (error) throw error;
      return (data ?? []) as unknown as AdminChannel[];
    },
  });
}

/** לכמה עובדים פעילים הערוץ מגיע. שאילתה אחת לכל הערוצים שבמסך. */
export function useChannelReach(channelIds: string[]) {
  const key = [...channelIds].sort().join(',');
  return useQuery({
    queryKey: ['channel-reach-map', key],
    enabled: channelIds.length > 0,
    queryFn: async (): Promise<Record<string, number>> => {
      const counts = await Promise.all(
        channelIds.map(async (id) => {
          const { data, error } = await supabase.rpc('channel_reach', { p_channel: id });
          if (error) throw error;
          return [id, data ?? 0] as const;
        }),
      );
      return Object.fromEntries(counts);
    },
  });
}

export function useChannelMutations() {
  const queryClient = useQueryClient();
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: KEY });
    void queryClient.invalidateQueries({ queryKey: ['channels'] });
    void queryClient.invalidateQueries({ queryKey: ['channel-reach-map'] });
  };

  /** הערוץ והקהל שלו נשמרים יחד (מיגרציה 088), ולא בשתי פעולות. */
  const save = useMutation({
    mutationFn: async (draft: ChannelDraft): Promise<string> => {
      const { data, error } = await supabase.rpc('save_channel', {
        // הטיפוסים הנוצרים מצהירים על p_id כמחרוזת, ו null שם הוא
        // "ערוץ חדש" — ערך חוקי שאין לו ביטוי בטיפוס
        p_id: draft.id as unknown as string,
        p_name: draft.name.trim(),
        p_description: draft.description.trim(),
        p_is_announcement_only: draft.isAnnouncementOnly,
        p_on_shift_only: draft.onShiftOnly,
        p_whole_network: draft.wholeNetwork,
        p_branch_ids: draft.branchIds,
        p_job_title_ids: draft.jobTitleIds,
        p_employee_ids: draft.employeeIds,
      });
      if (error) throw error;
      return data as string;
    },
    onSuccess: invalidate,
  });

  const setArchived = useMutation({
    mutationFn: async ({ id, isArchived }: { id: string; isArchived: boolean }) => {
      const { error } = await supabase
        .from('channels')
        .update({ is_archived: isArchived })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  return { save, setArchived };
}

/** פותחת שיחה אישית מול עובד, או מחזירה את הקיימת. */
export function useOpenDirectChannel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (employeeId: string): Promise<string> => {
      const { data, error } = await supabase.rpc('open_direct_channel', {
        p_employee: employeeId,
      });
      if (error) throw error;
      return data as string;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['channels'] }),
  });
}

export function audienceLabel(audience: Audience): string {
  const branch = audience.branches?.name;
  const job = audience.job_titles?.name;
  if (branch && job) return `${branch} · ${job}`;
  if (branch) return branch;
  if (job) return `${job}, בכל הסניפים`;
  return 'כל הרשת';
}
